import { Box } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * A tinted rounded square with an icon in it.
 *
 * This is where the panel gets its colour. The theme keeps surfaces white (or
 * near-black) and separates them with borders, which is right for tables and
 * maps but leaves a page of cards looking like a page of paragraphs. A tile is
 * small, carries one tone, and tells you what a card is about before you have
 * read its heading.
 *
 * The tint is a tenth of the tone, not the tone itself: a saturated block of
 * colour reads as an alarm, and in a panel where red means "this deletes
 * something" and amber means "this is owed", an alarm has to be earned.
 *
 * Five tones, which is the whole vocabulary — `accent` for the brand red,
 * `neutral` for the structural graphite, and the three the palette already
 * uses for meaning. Anything that needs a sixth probably needs a word instead.
 */
export type Tone = 'accent' | 'neutral' | 'success' | 'warning' | 'info';

export function IconTile({
  icon: Icon,
  tone = 'neutral',
  size = 42,
}: {
  icon: SvgIconComponent;
  tone?: Tone;
  size?: number;
}) {
  const theme = useTheme();
  const colour = toneColor(theme, tone);

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        // Not a circle: circles are for people (see `InitialsAvatar`), squares
        // are for things. Keeping them apart means a glance down a column
        // tells you whether the row is a person or a product.
        borderRadius: size / 3.2,
        color: colour,
        backgroundColor: alpha(colour, theme.palette.mode === 'dark' ? 0.18 : 0.1),
        border: `1px solid ${alpha(colour, 0.22)}`,
      }}
    >
      <Icon sx={{ fontSize: size * 0.5 }} />
    </Box>
  );
}

/** The one place a tone becomes a colour, so the two stay in step. */
export function toneColor(theme: Theme, tone: Tone): string {
  return {
    accent: theme.palette.secondary.main,
    neutral: theme.palette.text.secondary,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
  }[tone];
}
